import { Card, Text, Center, RingProgress } from '@mantine/core';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useContentAnalytics } from '@hooks/useContentAnalytics';
import { useTranslation } from 'react-i18next';
import styles from './Stats.module.scss';
import { getFlagEmoji } from '@/utils/languageUtils';
import { useMantineTheme } from '@mantine/core';

const COLORS = ['#339af0', '#51cf66', '#fcc419', '#ff6b6b', '#845ef7', '#00b894'];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
  }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ padding: '8px 12px' }}>
        <div style={{ fontSize: '14px' }}>{payload[0].name}</div>
        <div style={{ fontSize: '16px', fontWeight: 600 }}>{payload[0].value}</div>
      </div>
    );
  }
  return null;
};

const ChartPlaceholder = () => {
  const { t } = useTranslation();
  return (
    <Center style={{ height: '100%', flexDirection: 'column', gap: '1rem' }}>
      <RingProgress
        size={120}
        thickness={8}
        roundCaps
        sections={[
          { value: 40, color: COLORS[0] },
          { value: 30, color: COLORS[1] },
          { value: 20, color: COLORS[2] },
        ]}
      />
      <Text size="sm" c="dimmed">
        {t('common.noData')}
      </Text>
    </Center>
  );
};

export const LangPieChart = () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { langCounts } = useContentAnalytics();
  const data = Object.entries(langCounts).map(([key, value]) => ({
    name: `${getFlagEmoji(key)} ${key}`,
    value,
  }));

  const hasData = data.length > 0;

  if (!hasData) return null;

  return (
    <Card withBorder className={styles.stats__card}>
      <div className={styles.stats__label}>{t('stats.byLanguage')}</div>
      <div className={styles.stats__chart}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
                isAnimationActive
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                    stroke={theme.colors.dark ? theme.colors.dark[7] : theme.white}
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <ChartPlaceholder />
        )}
      </div>
    </Card>
  );
};
